import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { Grid, Paper, ShipsIcon, Stack, Tabs, Tab } from '@esgian/esgianui';
import { useParams, useSearchParams } from 'react-router-dom';
import PortStatisticsSection from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortStatisticsSection/PortStatisticsSection';
import PortWaitingTimeSection from './SubSections/PortWaitingTimeSection';
import PortOverviewSection from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortOverviewSection';
import OperatorTypeSelect from '@components/Inputs/OperatorTypeSelect/OperatorTypeSelect';
import PortOperatorStatisticsSection from '@components/Sections/ShipAnalytics/PortProfileSection/SubSections/PortOperatorStatisticsSection';
import { GEOGRAPHICAL_TYPES } from '@constants';
import { TextWithTooltipIcon } from '@components';
import { useSegment } from '@hooks';
import { getGeneralSlice, setGeographicalType } from '@store/features';
import { useDispatch, useSelector } from 'react-redux';
import { CommercialCategoryTypeSelect } from '@components/Inputs';
import { INSUFFICIENT_DATA_FOR_PORT_STATISTICS } from '@constants/tooltips';
import KpiBox from '@components/Display/KpiBox';
import { useQuery } from '@tanstack/react-query';
import { getPortCongestion, getPortStats } from '@api';
import { toast } from 'react-toastify';

function PortProfileSection({ portDetailsQuery = {} }) {
  const [section, setSection] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const { isRoRo, segment, segmentMinDate } = useSegment();
  const dispatch = useDispatch();
  const params = useParams();
  const { operatorType, commercialCategoryType } = useSelector(getGeneralSlice);

  const portStatsQuery = useQuery({
    queryKey: [
      'portStats',
      {
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        commercialCategoryId: commercialCategoryType,
        geoIds: [params.portId]
      }
    ],
    enabled: !!params.portId,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getPortStats(queryKey[1], signal)
        .then((result) => result)
        .catch((error) => {
          if (error.name !== 'AbortError') {
            console.log('error', error);
            toast.error('Unable to fetch port KPI data.', {
              position: 'top-right'
            });
          }
          return null;
        })
  });

  const portCongestionQuery = useQuery({
    queryKey: [
      'portCongestion',
      {
        GeoType: GEOGRAPHICAL_TYPES.PORT,
        OperatorTypeId: operatorType,
        SegmentTypeId: segment.id,
        GeoId: parseInt(params.portId),
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!params.portId && section === 3,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) =>
      getPortCongestion(
        {
          ...queryKey[1],
          start: moment.utc(segmentMinDate).toISOString(),
          end: moment.utc().toISOString()
        },
        signal
      )
        .then((result) => result)
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error('Unable to fetch port congestion data.', {
              position: 'top-right'
            });
          }
          return null;
        })
  });

  const handleSectionChange = (_, val) => {
    if (val === 2 && !portStatsQuery.data) {
      return;
    }
    searchParams.set('section', val);
    setSearchParams(searchParams);
    setSection(val);
  };

  useEffect(() => {
    dispatch(setGeographicalType(GEOGRAPHICAL_TYPES.PORT));
    setSection(parseInt(searchParams.get('section') || 1));
    return () => {
      searchParams.delete('section');
    };
  }, []);

  const { ytdNumPortCalls, prevYearYtdNumPortCalls, prevYearNumPortCalls } =
    portStatsQuery.data || {};
  const { portName = 'unknown' } = portDetailsQuery.data || {};
  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {isRoRo ? <OperatorTypeSelect /> : <CommercialCategoryTypeSelect />}
      </Grid>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={portStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={ytdNumPortCalls ?? ''}
            title={'Port Calls (YTD)'}
          />
          <KpiBox
            loading={portStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={prevYearYtdNumPortCalls ?? ''}
            title={`${moment().subtract(12, 'months').format('yyyy')} Port Calls YTD`}
          />
          <KpiBox
            loading={portStatsQuery.isFetching}
            icon={<ShipsIcon />}
            value={prevYearNumPortCalls ?? ''}
            title={`${moment().subtract(12, 'months').format('yyyy')} # Port Calls`}
          />
        </Stack>
      </Grid>
      <Grid item xs={12} id={'port-profile-overview'}>
        <Paper>
          <Tabs value={section ?? 1} onChange={handleSectionChange} variant="scrollable">
            <Tab value={1} label="Overview" />
            <Tab
              value={2}
              sx={{ cursor: portStatsQuery.data ? 'pointer' : 'default' }}
              disableRipple={!portStatsQuery}
              label={
                !portStatsQuery ? (
                  <TextWithTooltipIcon
                    color={'text.secondary'}
                    label={'PORT STATISTICS'}
                    tooltipText={INSUFFICIENT_DATA_FOR_PORT_STATISTICS}
                  />
                ) : (
                  'PORT STATISTICS'
                )
              }
            />
            <Tab value={3} label="Port Waiting Time" />
            <Tab value={4} label="Operator Statistics" />
          </Tabs>
        </Paper>
      </Grid>
      <Grid item xs={12}>
        {section === 1 && <PortOverviewSection portDetailsQuery={portDetailsQuery} />}
        {section === 2 && (
          <PortStatisticsSection
            portStatsQuery={portStatsQuery}
            portName={portName}
            portId={params.portId}
          />
        )}
        {section === 3 && (
          <PortWaitingTimeSection
            assetLevel={'port'}
            portCongestionData={portCongestionQuery.data}
            loading={portCongestionQuery.isFetching}
            portName={portName}
          />
        )}
        {section === 4 && <PortOperatorStatisticsSection portName={portName} />}
      </Grid>
    </Grid>
  );
}

PortProfileSection.propTypes = {
  portDetailsQuery: PropTypes.object
};

export default PortProfileSection;
